import API from '@/api';
import {
  sectionTypes,
  customSections,
  predefinedQuestionAnswers,
} from '@/constants/variables';
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';

/**
 * Shared logic for questionnaires
 */
export default {
  data() {
    return {
      sectionTypes,
      customSections,
      predefinedQuestionAnswers,
      untriggerable_question_snippet:
        '<p class="demo"><strong>Which of the following best suits your needs for your',
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),
    allDocumentsNeeded() {
      if (this.currentQuestionnaire.sections) {
        return this.documentChecklistRelevantSections
          .filter(s => this.sectionKey(s) === 'questionnaire')
          .map(section => this.documentChecklistItemsForSection(section))
          .flat();
      } else {
        return [];
      }
    },
    documentChecklistRelevantSections() {
      if (this.currentQuestionnaire && this.currentQuestionnaire.sections) {
        return this.currentQuestionnaire.sections.filter(
          section =>
            this.sectionTriggered(section) ||
            this.sectionHiddenViaUntriggerableQuestion(section),
        );
      } else {
        return [];
      }
    },
    questionnairePartiallySubmitted() {
      return (
        !this.currentQuestionnaire.completed &&
        this.currentQuestionnaire.sections.some(s =>
          s.questionnaire_questions.some(q => q.submitted),
        )
      );
    },
    sections() {
      if (this.currentQuestionnaire && this.currentQuestionnaire.sections) {
        return this.currentQuestionnaire.sections.filter(section =>
          this.sectionTriggered(section),
        );
      } else {
        return [];
      }
    },
    shownSections() {
      if (this.currentQuestionnaire && this.currentQuestionnaire.sections) {
        return this.currentQuestionnaire.sections.filter(
          section =>
            this.sectionTriggered(section) && this.sectionShown(section),
        );
      } else {
        return [];
      }
    },
    showUploadsOnlyInDocumentChecklist() {
      return (
        this.currentQuestionnaire.show_uploads_as_document_checklist ||
        (this.hideDocumentUploadsWithinSections &&
          this.hasDocumentChecklistSection)
      );
    },
    hasDocumentChecklistSection() {
      return this.currentQuestionnaire.sections.some(
        section => this.sectionKey(section) === 'document_checklist',
      );
    },
  },
  methods: {
    sectionHiddenViaUntriggerableQuestion(section) {
      if (!section.trigger_question_id) return false;
      let question = this.currentQuestionnaire.sections
        .map(s => s.questionnaire_questions)
        .flatten()
        .find(q => q.id === section.trigger_question_id);
      return this.questionIsNotTriggerableByRolloverItem(question);
    },
    questionIsNotTriggerableByRolloverItem(question) {
      return question.text.includes(this.untriggerable_question_snippet);
    },
    getQuestionById(questionId) {
      return this.currentQuestionnaire.sections
        .map(s => s.questionnaire_questions)
        .flatten()
        .find(q => q.id === questionId);
    },
    filteredComments(question) {
      if (this.currentUser.is_advisor) {
        return question.filtered_questionnaire_comments;
      } else {
        return question.filtered_questionnaire_comments.filter(
          c => !c.from_firm_user,
        );
      }
    },
    sectionShown(section) {
      // hide section to show uploads only in document checklist if all questions are uploads, but still relevant for document checklist
      if (
        this.showUploadsOnlyInDocumentChecklist &&
        this.sectionKey(section) === 'questionnaire' &&
        section.questionnaire_questions
          .filter(q => q.order)
          .every(q => q.question_type === 'upload')
      )
        return false;
      return true;
    },
    sectionTriggered(section) {
      // Not a conditional section
      if (!section.trigger_question_id) return true;
      let question = this.currentQuestionnaire.sections
        .map(s => s.questionnaire_questions)
        .flatten()
        .find(q => q.id === section.trigger_question_id);
      let questionSection = this.currentQuestionnaire.sections.find(
        s => s.id === question.section_id,
      );
      if (question.text === this.untriggerable_question_snippet) return true;
      return (
        question &&
        question.answer &&
        this.sectionTriggered(questionSection) &&
        ((question.question_type === 'multiple' &&
          question.answer
            .split('&!&')
            .some(a => section.trigger_answer.split('%!%').includes(a))) ||
          section.trigger_answer.split('%!%').includes(question.answer))
      );
    },
    findSectionByKey(key) {
      return this.currentQuestionnaire.sections.find(
        s => this.sectionKey(s) === key,
      );
    },
    formattedAnswer(question) {
      if (!question.answer) return 'Unanswered';
      if (this.predefinedQuestionAnswers.includes(question.answer))
        return question.answer;
      if (question.question_type === 'multiple')
        return `Selected '${question.answer.replace('&!&', ', ')}'`;
      if (question.question_type === 'select')
        return `Selected '${question.answer}'`;
      if (question.question_type === 'upload') {
        let list = question.answer.split('&!&');
        list.shift();
        return `Uploaded ${list.join(', ')}'`;
      } else {
        return question.answer;
      }
    },
    deliverySection(questionnaire) {
      return questionnaire.sections.find(
        section => this.sectionKey(section) === 'delivery',
      );
    },
    sectionKey(section) {
      return this.matchSection(section.section_type).key;
    },
    findSection(question) {
      return this.currentQuestionnaire.sections.find(
        s => s.id === question.section_id,
      );
    },
    matchSection(sectionType) {
      const section = this.sectionTypes.find(t => t.id === sectionType);
      if (section) {
        return section;
      } else {
        return this.sectionTypes.find(t => t.key === 'custom');
      }
    },
    sectionCompleted(questionnaire, section) {
      if (this.sectionKey(section) === 'questionnaire') {
        return this.filteredSectionQuestionsToBeAnswered(
          questionnaire,
          section,
        ).every(q => this.questionAnswered(q));
      } else if (this.sectionKey(section) === 'document_checklist') {
        return this.allDocumentsNeeded.every(q => this.questionAnswered(q));
      } else {
        return section.marked_completed;
      }
    },
    firstThreeIncompleteSections(questionnaire) {
      return questionnaire.sections
        .filter(section => !section.marked_completed && section.triggered)
        .slice(0, 3);
    },
    questionAnswered(question) {
      if (question.question_type === 'entries') {
        return (
          ((!isEmpty(question.answer) && question.answer !== '0') ||
            question.optional) &&
          !question.answer_by
        );
      } else {
        return !isEmpty(question.answer) || question.optional;
      }
    },
    filteredSectionQuestionsToBeAnswered(questionnaire, section) {
      if (questionnaire.show_uploads_as_document_checklist) {
        return section.questionnaire_questions.filter(
          q =>
            q.question_type !== 'upload' &&
            this.sectionQuestionsToBeAnswered(section, q),
        );
      } else {
        return section.questionnaire_questions.filter(q =>
          this.sectionQuestionsToBeAnswered(section, q),
        );
      }
    },

    documentChecklistItemsForSection(section) {
      return section.questionnaire_questions.filter(q => {
        return (
          q.question_type === 'upload' &&
          q.answer !== 'Marked Completed' &&
          this.sectionQuestionsToBeAnswered(section, q)
        );
      });
    },

    sectionQuestionsToBeAnswered(section, question) {
      if (!question.follow_up_question_id) return true;
      if (!this.getParentQuestion(section, question)) return false;
      if (this.getParentQuestion(section, question).follow_up_question_id) {
        return (
          this.questionTriggered(
            section,
            this.getParentQuestion(section, question),
          ) &&
          this.questionTriggered(section, question) &&
          this.getParentQuestion(section, question).question_type !== 'entries'
        );
      }
      return (
        this.questionTriggered(section, question) &&
        this.getParentQuestion(section, question).question_type !== 'entries'
      );
    },

    getParentQuestion(section, question) {
      return section.questionnaire_questions.find(
        q => q.id === question.follow_up_question_id,
      );
    },
    questionTriggered(section, question) {
      let parent = section.questionnaire_questions.find(
        q => q.id === question.follow_up_question_id,
      );
      return (
        parent &&
        parent.answer &&
        ((parent.question_type === 'multiple' &&
          parent.answer
            .split('&!&')
            .includes(question.trigger_for_follow_up_question)) ||
          parent.answer === question.trigger_for_follow_up_question)
      );
    },
    onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    },
    getDocumentName(document) {
      return document.split('/')[document.split('/').length - 1];
    },
    async downloadDocument(document) {
      try {
        const response = await API.integration.downloadDocument({
          companyId: this.currentCompany.id,
          link: this.currentCompany.upload_link.concat('/', document),
        });
        if (response.data.document) {
          this.downloadDocumentByLink(
            response.data.document.data.link,
            this.getDocumentName(document),
          );
        } else {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem downloading the document.',
          });
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      }
    },
    async downloadDocumentByLink(url, name) {
      let response = await this.$http.get(url, { responseType: 'arraybuffer' });
      let blob = new Blob([response.data], {
        type: this.get(response.headers, 'content-type'),
      });
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = name;
      link.click();
    },
    reviewAndSubmitSectionFinished(questionnaire) {
      const reviewAndSubmitSection = questionnaire.sections.find(
        s => this.sectionKey(s) === 'review_and_submit',
      );
      return reviewAndSubmitSection && reviewAndSubmitSection.marked_completed;
    },
  },
};
